import { UrlObject } from 'node:url';

import { Flex, Link as ChakraLink } from '@chakra-ui/react';
import { getLocaleInURL, hasLocaleInURL } from 'helpers/localeURL';
import NextLink from 'next/link';
import React, { FunctionComponent, ReactElement, ReactNode } from 'react';

import { LinkType } from '../../lib/enums/LinkType';
import { IconObject } from '../../lib/Icons';
import { IconComp } from './IconComp';

interface LinkProps {
  href: UrlObject | string;
  children: ReactNode;
  type: LinkType;
  isDisabled?: boolean;
  iconName?: keyof typeof IconObject;
  colorScheme?: string;
  weight?: string;
  download?: string;
  ariaLabel?: string;
  shallow?: boolean;
  isExternal?: boolean;
  as?: string;
  locale?: string;
  onClick?: (e) => void;
  tabIndex?: number;
  navLinkMode?: boolean;
  positionLock?: boolean;
  isPrDisabled?: boolean;
}

export const Link: FunctionComponent<LinkProps> = ({
  href,
  isDisabled = false,
  children,
  type,
  iconName,
  colorScheme = 'cyan.web',
  weight = 'normal',
  download,
  ariaLabel,
  shallow = false,
  isExternal = false,
  as,
  locale,
  onClick,
  navLinkMode,
  positionLock,
  isPrDisabled,
}): ReactElement => {
  const localeFromURL = hasLocaleInURL(href) && getLocaleInURL(href);
  const modHref =
    localeFromURL == 'string' && localeFromURL.trim().length > 0 && typeof href == 'string'
      ? href.replace(localeFromURL, '')
      : href;
  // if (href == '/[...slug].tsx') {
  //   console.log('Link href', href);
  //   console.log('Locale passed to href', locale + '');

  //   console.log('Locale from URL', localeFromURL);
  // }
  return isDisabled ? (
    getStyledChakraLink(
      isDisabled,
      children,
      type,
      ariaLabel,
      iconName,
      colorScheme,
      weight,
      download,
      isExternal,
      as,
      onClick,
      navLinkMode,
      positionLock,
      isPrDisabled,
    )
  ) : (
    <NextLink
      legacyBehavior
      passHref
      shallow={shallow}
      href={modHref}
      as={as ?? href}
      locale={locale ?? process.env.NEXT_PUBLIC_DEFAULT_LOCALE!}
      // prefetch={true}
    >
      {getStyledChakraLink(
        isDisabled,
        children,
        type,
        ariaLabel,
        iconName,
        colorScheme,
        weight,
        download,
        isExternal,
        as ?? (href as string),
        onClick,
        navLinkMode,
        positionLock,
        isPrDisabled,
      )}
    </NextLink>
  );
};

export const getStyledChakraLink = (
  isDisabled: boolean,
  children: ReactNode,
  type: LinkType,
  ariaLabel?: string,
  iconName?: keyof typeof IconObject,
  colorScheme?: string,
  weight?: string,
  download?: string,
  isExternal?: boolean,
  as?: string,
  onClick?: (e) => void,
  navLinkMode?: boolean,
  positionLock?: boolean,
  isPrDisabled?: boolean,
) => {
  return (
    <ChakraLink
      data-group='baselinkgrp'
      position='relative'
      left={LinkType.Menu === type ? (positionLock == true ? '0' : '-10px') : undefined}
      transition='transform 0.3s'
      variant={isDisabled ? type + 'Disabled' : type}
      colorScheme={LinkType.Item === type ? 'black' : colorScheme}
      download={download}
      aria-label={ariaLabel}
      role='button'
      pointerEvents={isDisabled ? 'none' : 'auto'}
      fontWeight={weight}
      textStyle={
        navLinkMode
          ? { base: 'mobile.body.l', lg: 'desktop.body.s' }
          : LinkType.Menu === type
          ? 'desktop.button'
          : { base: 'mobile.link', lg: 'desktop.link' }
      }
      textDecor={'none !important'} //must override default underline
      isExternal={isExternal}
      pr={isPrDisabled ? '0px' : '30px'}
      py='0'
      _hover={LinkType.Menu === type && !navLinkMode ? { transform: 'translateX(30px)', color: 'cyan.web' } : undefined}
      _focus={
        LinkType.Menu === type && !navLinkMode
          ? { transform: 'translateX(30px)', color: 'cyan.web' }
          : { border: 'none' }
      }
      onClick={(e) => onClick && onClick(e)}
    >
      {iconName && LinkType.SecondaryButton === type && !isDisabled && (
        <IconComp
          iconName={iconName}
          chakraStyle={{
            display: LinkType.SecondaryButton === type ? 'flex' : 'none',

            "[data-group='baselinkgrp']:hover &,[data-group='baselinkgrp']:focus-within &": {
              display: 'flex',
              pr: '5px',
              pl: '15px',
            },
            ...(navLinkMode ? { position: 'absolute', left: 0 } : {}),
            pr: 'desktop.xxs',
            transition: 'all .5s ease-in-out',
            // _groupHover: { display: 'flex', pr: '5px', pl: '15px' },
            // _groupFocus: { display: 'flex' },
            path: {
              stroke: isDisabled ? 'mountain.60' : colorScheme,
            },
          }}
        ></IconComp>
      )}

      {iconName && LinkType.Menu === type && !isDisabled && (
        <IconComp
          iconName={iconName}
          chakraStyle={{
            "[data-group='baselinkgrp']:hover &,[data-group='baselinkgrp']:focus-within &": {
              opacity: '1',
              path: {
                stroke: isDisabled ? 'mountain.60' : colorScheme,
              },
            },
            ...(navLinkMode ? { position: 'absolute', left: 0 } : {}),
            svg: {
              height: '25px',
              width: '25px',
            },
            opacity: '0',
            pr: 'xxs',
            transition: navLinkMode ? 'all .2s ease-in-out' : 'all .5s ease-in-out',
            // _groupHover: {
            //   opacity: '1',
            //   path: {
            //     stroke: isDisabled ? 'mountain.60' : colorScheme,
            //   },
            // },
            // _groupFocus: {
            //   opacity: '1',
            //   path: {
            //     stroke: isDisabled ? 'mountain.60' : colorScheme,
            //   },
            // },
            path: {
              stroke: isDisabled ? 'mountain.60' : 'mountain.base',
            },
          }}
        ></IconComp>
      )}
      <Flex
        transition={navLinkMode ? 'transform 0.3s' : undefined}
        sx={
          navLinkMode
            ? {
                "[data-group='baselinkgrp']:hover &,[data-group='baselinkgrp']:focus-within &": {
                  transform: 'translateX(30px)',
                  color: 'cyan.web',
                },
              }
            : {}
        }
        aria-label={ariaLabel}
      >
        {children}
      </Flex>
    </ChakraLink>
  );
};
